// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import * as Sentry from '@sentry/react';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { AddInvoiceForm } from '@/billing/components/add-invoice-form';
import { useAddInvoice } from '@/billing/hooks/use-add-invoice';
import { AddInvoiceFormData } from '@/billing/schemas/add-invoice-form-schema';
import { SupportSnackbar } from '@/components/support-snackbar';
import { useToast } from '@/contexts/toast-context';
import { getCustomers } from '@/customer/api/get-customers';
import { getPaymentTerms } from '@/payment-term/api/get-payment-terms';
import { useSparseProjectList } from '@/project/hooks/use-sparse-project-list';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

interface AddInvoiceDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const FORM_ID = 'add-invoice-form';

export function AddInvoiceDialog(props: AddInvoiceDialogProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [showSnackbar, setShowSnackbar] = useState(false);

  const customersQuery = useQuery({
    queryKey: ['customer', { isCustomer: true }],
    queryFn: () => getCustomers({ isCustomer: true }),
    enabled: props.isOpen,
  });
  const customers = useMemo(
    () => customersQuery.data?.customers ?? [],
    [customersQuery.data]
  );

  const paymentTermsQuery = useQuery({
    queryKey: ['payment-term'],
    queryFn: getPaymentTerms,
    enabled: props.isOpen,
  });
  const paymentTermOptions = useMemo(
    () => paymentTermsQuery.data?.paymentTerms ?? [],
    [paymentTermsQuery.data]
  );

  // Kick off the sparse-projects fetch at the dialog level so all three of
  // (customers, payment terms, projects) load in parallel and the spinner
  // covers the full picture. The form reads this same query via its own
  // useSparseProjectList call, hitting the cache.
  const projectsQuery = useSparseProjectList({ enabled: props.isOpen });

  const { user } = useCurrentUser();
  const orgDefaultPaymentTerm = user.organization.defaultPaymentTerm;

  const mutation = useAddInvoice({
    onSuccess: (invoiceKey) => {
      showToast({
        element: <Typography>Draft invoice created</Typography>,
        severity: 'success',
        duration: 3000,
      });
      props.onClose();
      navigate({
        to: '/billing/invoices/$invoiceKey',
        params: { invoiceKey },
      });
    },
    onError: () => {
      setShowSnackbar(true);
    },
  });

  const handleAfterClose = useCallback(() => {
    setShowSnackbar(false);
    mutation.reset();
  }, [mutation]);

  const handleSubmit = useCallback(
    (data: AddInvoiceFormData) => {
      // Schema refines guarantee customer, project, and dueDate are non-null
      // by the time onSubmit fires. If they aren't, the form is broken — fail
      // loudly so we hear about it.
      if (
        data.customer === null ||
        data.project === null ||
        data.dueDate === null
      ) {
        const err = new Error(
          'AddInvoiceForm onSubmit received null customer, project, or dueDate after schema validation'
        );
        Sentry.captureException(err);
        throw err;
      }
      const poTrimmed = data.purchaseOrderNumber.trim();
      mutation.mutate({
        customerKey: data.customer.key,
        projectKey: data.project.key,
        invoiceDate: data.invoiceDate,
        dueDate: data.dueDate,
        paymentTerms: data.paymentTerm?.label ?? null,
        purchaseOrderNumber: poTrimmed === '' ? null : poTrimmed,
      });
    },
    [mutation]
  );

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={props.isOpen}
      onClose={props.onClose}
      scroll="paper"
      TransitionProps={{ onExited: handleAfterClose }}
    >
      <DialogTitle>Create manual invoice</DialogTitle>
      <DialogContent dividers>
        {customersQuery.isPending ||
        paymentTermsQuery.isPending ||
        projectsQuery.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <AddInvoiceForm
            customers={customers}
            paymentTermOptions={paymentTermOptions}
            orgDefaultPaymentTerm={orgDefaultPaymentTerm}
            formId={FORM_ID}
            isDisabled={mutation.isPending}
            isOpen={props.isOpen}
            onSubmit={handleSubmit}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose} disabled={mutation.isPending}>
          Cancel
        </Button>
        <Button
          type="submit"
          form={FORM_ID}
          variant="contained"
          color="primary"
          disabled={mutation.isPending}
          startIcon={
            mutation.isPending ? (
              <CircularProgress size={16} color="inherit" />
            ) : undefined
          }
        >
          Create draft
        </Button>
      </DialogActions>
      <SupportSnackbar
        isOpen={showSnackbar}
        onClose={() => setShowSnackbar(false)}
        message="An error occurred creating the invoice."
      />
    </Dialog>
  );
}
